import { useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { useRole } from "@/hooks/use-role";
import { useFreelancerProfile } from "@/lib/hooks/use-freelancer-profile";
import { useUnifiedCourses } from "@/lib/hooks/use-unified-courses";
import { usePortfolioProjects } from "@/lib/hooks/use-portfolio-projects";
import { useWorkExperience } from "@/lib/hooks/use-work-experience";
import { useProfileCompletion } from "@/lib/hooks/use-profile-completion";
import {
  WelcomeBanner,
  TrainingProgressBanner,
  QuickActions,
  ProfileCompletionCard,
  WhatsNextTimeline,
  RecentMessagesCard,
} from "@/components/dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { BookOpen, GraduationCap, Briefcase, CheckCircle2, ArrowRight, PlayCircle, Eye } from "lucide-react";
import { SlideIn, FadeIn, StaggerContainer, StaggerItem } from "@/components/motion";
import { emailService } from '@/lib/api/services/email.service';
import { CertificationProfileBanner } from '@/components/certification/CertificationProfileBanner';

type ReviewStatus = 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected' | null;

// Get dynamic welcome subtitle based on review status and profile completion
function getWelcomeSubtitle(reviewStatus: ReviewStatus, isProfileComplete: boolean): string {
  if (reviewStatus === 'approved') {
    return "Your profile is approved. You're ready to receive opportunities!";
  }

  if (reviewStatus === 'submitted') {
    return "Your profile is under review. We'll notify you once it's approved.";
  }

  if (reviewStatus === 'changes_requested') {
    return "Action required: Please address the requested changes to your profile.";
  }

  if (reviewStatus === 'rejected') {
    return "Your profile was not approved. Please update and resubmit for review.";
  }

  // draft or null - not yet submitted
  if (isProfileComplete) {
    return "Your profile is ready! Submit it for review to start receiving opportunities.";
  }

  return "Complete your profile to submit for review and start receiving opportunities.";
}

export default function FreelancerDashboard() {
  const { user } = useUser();
  const { freelancerProfileId, isLoading: isLoadingRole } = useRole();

  // Wait for role context to finish loading before attempting to fetch profile data
  const {
    freelancerProfile,
    isLoading: isLoadingProfile,
    isEnabled,
    reviewStatus,
  } = useFreelancerProfile(freelancerProfileId, { enableMutations: false });

  const { courses, isLoading: isLoadingCourses } = useUnifiedCourses();

  // Only fetch portfolio projects if we have a valid profile ID
  const { projects: portfolioProjects } = usePortfolioProjects(
    freelancerProfileId ?? undefined
  );

  // Fetch work experience entries
  const { experiences } = useWorkExperience(freelancerProfileId ?? undefined);

  // Calculate course progress
  const enrolledCourses = courses?.filter((c) => c.enrollment) || [];
  const completedCourses = enrolledCourses.filter(
    (c) => c.enrollment?.status === "completed"
  ).length;
  const totalCourses = courses?.length || 0;
  const inProgressCourses = enrolledCourses.filter(
    (c) => c.enrollment?.status === "active"
  );

  const courseProgress = {
    totalCourses,
    completedCourses,
    allCompleted: completedCourses >= totalCourses && totalCourses > 0,
  };

  // Calculate profile completion
  const profileCompletion = useProfileCompletion(
    freelancerProfile,
    courseProgress,
    portfolioProjects?.length || 0,
    experiences?.length || 0
  );

  // Send email when profile reaches 100% completion (only once)
  useEffect(() => {
    if (!user?.id || !profileCompletion.isComplete || !freelancerProfile?.email) return;
    
    const storageKey = `genius-profile-completion-email-sent-${user.id}`;
    const hasSentEmail = localStorage.getItem(storageKey) === 'true';
    
    if (!hasSentEmail) {
      // Mark as sent immediately to prevent duplicate sends
      localStorage.setItem(storageKey, 'true');
      
      // Send completion email (non-blocking)
      emailService.sendGeniusProfileCompletedEmail(freelancerProfile.email).catch((error) => {
        console.error('Failed to send profile completion email:', error);
        // Remove the flag on error so we can retry
        localStorage.removeItem(storageKey);
      });
    }
  }, [profileCompletion.isComplete, freelancerProfile?.email, user?.id]);

  // Include role loading state and handle null profile ID
  const isLoading = isLoadingRole || isLoadingProfile || isLoadingCourses || freelancerProfileId === null;

  if (isLoading) {
    return (
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-32 w-full rounded-3xl" />
          <Skeleton className="h-24 w-full rounded-3xl" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-64 rounded-[40px] lg:col-span-2" />
            <Skeleton className="h-64 rounded-[40px]" />
          </div>
        </div>
      </div>
    );
  }

  const displayName = freelancerProfile?.display_name || "Expert";
  const showTrainingBanner = !courseProgress.allCompleted && totalCourses > 0;

  // Find the next course to continue
  const nextCourse = inProgressCourses[0] || courses?.find(c => !c.enrollment);

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Welcome Banner */}
        <SlideIn direction="down">
          <WelcomeBanner
            name={displayName.split(" ")[0]}
            subtitle={getWelcomeSubtitle(
              reviewStatus as ReviewStatus,
              profileCompletion.isComplete
            )}
          />
        </SlideIn>

        {/* Training Progress Banner */}
        {showTrainingBanner && (
          <FadeIn delay={0.2}>
            <TrainingProgressBanner
              completedCourses={completedCourses}
              totalCourses={totalCourses}
              reviewStatus={reviewStatus as 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected' | null}
            />
          </FadeIn>
        )}

        {/* Certification Banner */}
        <CertificationProfileBanner />

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Training Progress Card */}
            <SlideIn direction="up" delay={0.3}>
              <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900 flex items-center gap-3">
                    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                      <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5 text-[#4669ed]" />
                    </div>
                    Training Progress
                  </h2>
                  <Link
                    to="/genius/courses"
                    className="text-sm font-medium text-[#4669ed] hover:underline flex items-center gap-1"
                  >
                    View All Courses
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Course Stats */}
                <StaggerContainer staggerDelay={0.08} delayChildren={0.1} className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
                  <StaggerItem className="bg-slate-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-slate-900">{totalCourses}</div>
                    <div className="text-xs sm:text-sm text-slate-500 mt-1">Total</div>
                  </StaggerItem>
                  <StaggerItem className="bg-[rgba(70,105,237,0.05)] rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-[#4669ed]">{inProgressCourses.length}</div>
                    <div className="text-xs sm:text-sm text-slate-500 mt-1">In Progress</div>
                  </StaggerItem>
                  <StaggerItem className="bg-emerald-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-emerald-600">{completedCourses}</div>
                    <div className="text-xs sm:text-sm text-slate-500 mt-1">Completed</div>
                  </StaggerItem>
                </StaggerContainer>

              {/* Continue Course or Start Learning */}
              {nextCourse ? (
                <div className="bg-slate-50 rounded-xl sm:rounded-2xl p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                    {/* Thumbnail */}
                    <div className="w-full sm:w-20 h-32 sm:h-20 rounded-lg sm:rounded-xl overflow-hidden flex-shrink-0">
                      {nextCourse.thumbnail_url ? (
                        <img
                          src={nextCourse.thumbnail_url}
                          alt={nextCourse.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                          <BookOpen className="w-8 h-8 text-[#4669ed]/40" />
                        </div>
                      )}
                    </div>

                    {/* Course Info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                        {nextCourse.enrollment ? 'Continue Learning' : 'Start Learning'}
                      </div>
                      <h3 className="font-semibold text-slate-900 line-clamp-1">{nextCourse.title}</h3>
                      {nextCourse.enrollment && (
                        <div className="flex items-center gap-2 mt-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                            <div
                              className="bg-[#4669ed] h-full rounded-full transition-all"
                              style={{ width: `${Math.round(Number(nextCourse.enrollment.progress_percent) || 0)}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500">
                            {Math.round(Number(nextCourse.enrollment.progress_percent) || 0)}%
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    <Link
                      to={`/genius/course/${nextCourse.slug}`}
                      className="w-full sm:w-auto flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-white font-medium text-sm"
                      style={{
                        background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                      }}
                    >
                      <PlayCircle className="w-4 h-4" />
                      {nextCourse.enrollment ? 'Continue' : 'Start'}
                    </Link>
                  </div>
                </div>
              ) : totalCourses === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No courses available yet.</p>
                </div>
              ) : (
                <div className="bg-emerald-50 rounded-2xl p-6 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">All Courses Completed!</h3>
                  <p className="text-sm text-slate-500">You've completed all available training courses.</p>
                </div>
              )}
              </div>
            </SlideIn>

            {/* Quick Actions and Profile Views */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <SlideIn direction="up" delay={0.4} className="md:col-span-2">
                <Card className="rounded-3xl border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-[#4669ed]" />
                      </div>
                      Quick Actions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <QuickActions 
                      columns={2} />
                  </CardContent>
                </Card>
              </SlideIn>

              <SlideIn direction="up" delay={0.5}>
                <Card className="rounded-3xl border-slate-200 h-full flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-xl flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-[rgba(70,105,237,0.10)] flex items-center justify-center">
                        <Eye className="w-5 h-5 text-[#4669ed]" />
                      </div>
                      Profile Views
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col items-center justify-center">
                    {/* Large number */}
                    <div className="text-5xl font-bold text-slate-900 mb-2">
                      {freelancerProfile?.view_count ?? 0}
                    </div>
                    {/* Label below */}
                    <div className="text-sm text-slate-500">
                      Profile Views
                    </div>
                  </CardContent>
                </Card>
              </SlideIn>
            </div>

            {/* What's Next Timeline - Shows journey steps when not approved */}
            <WhatsNextTimeline
              reviewStatus={reviewStatus as ReviewStatus}
              isProfileComplete={profileCompletion.isComplete}
            />
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Profile Completion Card */}
            <SlideIn direction="left" delay={0.3}>
              <ProfileCompletionCard
                name={displayName}
                profilePictureUrl={freelancerProfile?.profile_picture_url}
                completionPercentage={profileCompletion.percentage}
                items={profileCompletion.items}
                reviewStatus={reviewStatus}
                isEnabled={isEnabled}
              />
            </SlideIn>

            {/* Recent Messages */}
            <SlideIn direction="left" delay={0.2}>
              <RecentMessagesCard chatRoute="/genius/chat" maxConversations={4} />
            </SlideIn>

            {/* Portfolio Preview */}
            <SlideIn direction="left" delay={0.4}>
              <div className="bg-white rounded-2xl sm:rounded-3xl lg:rounded-[40px] border border-slate-200 p-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-slate-900">Portfolio</h3>
                  <Link
                    to="/genius/profile#portfolio"
                    className="text-sm text-[#4669ed] hover:underline"
                  >
                    Manage
                  </Link>
                </div>

                {portfolioProjects && portfolioProjects.length > 0 ? (
                  <div className="space-y-3">
                    {portfolioProjects.slice(0, 3).map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50"
                      >
                        <div className="w-10 h-10 rounded-lg bg-[rgba(70,105,237,0.10)] flex items-center justify-center flex-shrink-0">
                          <Briefcase className="w-5 h-5 text-[#4669ed]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 line-clamp-1">
                            {project.title}
                          </p>
                          {project.description && (
                            <p className="text-xs text-slate-500 line-clamp-1">{project.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                    {portfolioProjects.length > 3 && (
                      <p className="text-xs text-center text-slate-500">
                        +{portfolioProjects.length - 3} more projects
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Briefcase className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500 mb-3">No projects yet</p>
                    <Link
                      to="/genius/profile#portfolio"
                      className="text-sm font-medium text-[#4669ed] hover:underline"
                    >
                      Add your first project
                    </Link>
                  </div>
                )}
              </div>
            </SlideIn>
          </div>
        </div>
      </div>
    </div>
  );
}
